import React, { useEffect, useState } from "react";
import Panel from "../../atoms/Panel/Panel";
import Button from "../../atoms/Button/Button";
import { supabase } from "../../../lib/supabase/supabaseClient";
import { apiPost } from "../../../services/api/generic.api";
import { Message } from "../../organisms/AiChatbot/AiChatbot";
import { useSelector, useDispatch } from "react-redux";
import RichTextEditor from "../../organisms/RichTextEditor/RichTextEditor";
import TextField from "../../atoms/TextField/TextField";

interface AiReportCustomPromptProps {
  id: string;
  title?: string;
  prompt?: string;
  formattedReport?: string;
  withInput?: boolean;
  inputPlaceholder?: string;
}

const AiReportCustomPrompt = (props: AiReportCustomPromptProps) => {
  const { id, title, prompt, formattedReport, withInput, inputPlaceholder } =
    props;

  const userId = useSelector((state: any) => state.settings.user.userID);
  const dispatch = useDispatch();

  const [isExpanded, setIsExpanded] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState("");

  const channelId = `chat-room-${userId}-${id}`;

  useEffect(() => {
    const channel = supabase.channel(channelId, {
      config: { broadcast: { self: true } },
    });

    channel.on("broadcast", { event: "message" }, (payload) => {
      const data = payload.payload;
      if (!data) return;

      setMessages((prev) => {
        if (data.role === "assistant" && data.partial) {
          const last = prev[prev.length - 1];
          if (last?.role === "assistant") {
            if (last.content.startsWith("Premýšľam...")) {
              return [...prev.slice(0, -1), { ...last, content: data.content }];
            }
            const newMessages = [
              ...prev.slice(0, -1),
              { ...last, content: last.content + data.content },
            ];
            return newMessages;
          } else {
            return [...prev, data];
          }
        }
        if (data.role === "user") {
          return [
            ...prev,
            data,
            {
              role: "assistant",
              content: "Premýšľam...",
            },
          ];
        }
        if (data.role === "assistant" && !data.partial) {
          const last = prev[prev.length - 1];
          if (last?.role === "assistant") {
            return [
              ...prev.slice(0, -1),
              {
                ...data,
                content: last.content + data.content,
                citations: data.citations,
              },
            ];
          }
        }
        return [...prev, data];
      });
    });

    channel.subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [channelId]);

  const sendMessage = async () => {
    if (!prompt) return;

    setIsExpanded(true);

    let finalPrompt = prompt;

    if (withInput) {
      finalPrompt += `${inputValue}`;
    }

    await supabase.channel(channelId).httpSend("message", {
      role: "user",
      content: finalPrompt,
    });

    const messagesWithoutFirstMessage = messages.filter(
      (msg, index) => index !== 0,
    );

    const newMessages = [
      ...messagesWithoutFirstMessage,
      { role: "user", content: finalPrompt },
    ];

    await apiPost(dispatch, "api/chat", {
      messages: newMessages,
      formattedReport,
      channelId,
    });
  };

  return (
    <Panel
      title={title}
      isExpandable={messages.length !== 0}
      isExpanded={isExpanded}
      panelActions={
        messages.length === 0 ? (
          <>
            {withInput && (
              <TextField
                placeholder={inputPlaceholder}
                value={inputValue}
                onChange={setInputValue}
                onEnter={sendMessage}
              />
            )}
            <Button
              type="secondary"
              className="dui-ai-report-ask-chat"
              onClick={sendMessage}
              disabled={withInput && inputValue === ""}
            >
              Spýtať sa
            </Button>
          </>
        ) : undefined
      }
    >
      <div>
        {messages.map(
          (msg, idx) =>
            msg.role === "assistant" && (
              <div key={idx} className="dui-chat-message-bot">
                <RichTextEditor markdown={msg.content} readonly />
                {/* {msg.citations && <ChatCitations citations={msg.citations} />} */}
              </div>
            ),
        )}
      </div>
    </Panel>
  );
};

export default AiReportCustomPrompt;
